import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAudioPlayer } from '@/context/AudioContext';
import { radioStations, type RadioStation } from '@/mocks/radios';
import { historyEntries } from '@/mocks/listenerHistory';
import { technicalStreamStatusLabels, technicalStreamStatusMessages, technicalStreamStatusColors } from '@/types/streaming';
import type { TechnicalStreamStatus } from '@/types/streaming';

type SleepOption = 0 | 15 | 30 | 45 | 60;

const SLEEP_OPTIONS: { value: SleepOption; label: string }[] = [
  { value: 0, label: 'Off' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
];

function formatSleepTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface MobileFullPlayerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileFullPlayer({ isOpen, onClose }: MobileFullPlayerProps) {
  const {
    currentStation, isPlaying, volume, toggle, stop, setVolume,
    changeStation, stations, fallbackInfo, dismissFallback,
    streamError, dismissStreamError,
  } = useAudioPlayer();

  const [favorite, setFavorite] = useState(false);
  const [sleepTimer, setSleepTimer] = useState<SleepOption>(0);
  const [sleepRemaining, setSleepRemaining] = useState(0);
  const [showStationSwitcher, setShowStationSwitcher] = useState(false);
  const [stationSearch, setStationSearch] = useState('');
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setAnimating(true));
    } else {
      setAnimating(false);
    }
  }, [isOpen]);

  /* ── Sleep timer countdown ── */
  useEffect(() => {
    if (sleepTimer === 0 || sleepRemaining <= 0) {
      setSleepRemaining(0);
      return;
    }
    const interval = setInterval(() => {
      setSleepRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          stop();
          onClose();
          setSleepTimer(0);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [sleepTimer, sleepRemaining, stop, onClose]);

  const handleSleepTimer = useCallback((option: SleepOption) => {
    setSleepTimer(option);
    setSleepRemaining(option > 0 ? option * 60 : 0);
  }, []);

  /* ── Recent stations from unified history ── */
  const recentStations = useMemo(() => {
    const seen = new Set<string>();
    const recent: RadioStation[] = [];
    const radioEntries = historyEntries.filter((e) => e.type === 'radio');
    for (const entry of radioEntries) {
      if (seen.has(entry.title)) continue;
      seen.add(entry.title);
      const matched = radioStations.find((s) => s.name === entry.title);
      if (matched && matched.id !== currentStation?.id) {
        recent.push(matched);
      }
      if (recent.length >= 5) break;
    }
    return recent;
  }, [currentStation]);

  /* ── Filtered stations for switcher ── */
  const filteredStations = useMemo(() => {
    const q = stationSearch.toLowerCase().trim();
    if (!q) return stations;
    return stations.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.frequency.includes(q) ||
        s.city.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q),
    );
  }, [stationSearch, stations]);

  /* ── Share handler ── */
  const handleShare = useCallback(async () => {
    if (!currentStation) return;
    const shareData = {
      title: currentStation.name,
      text: `${currentStation.name} · ${currentStation.frequency} — Escuchando en RadioFrecuenciaWeb`,
      url: `${window.location.origin}/radio/${currentStation.slug || currentStation.frequency.replace('.', '-')}`,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
      } catch { /* clipboard denied */ }
    }
  }, [currentStation]);

  /* ── Parse metadata for program/host ── */
  const metadata = useMemo(() => {
    if (!currentStation?.currentMetadata) return { program: null, host: null };
    const parts = currentStation.currentMetadata.split(' - ');
    if (parts.length >= 2) {
      return { program: parts.slice(0, -1).join(' - '), host: parts[parts.length - 1] };
    }
    return { program: currentStation.currentMetadata, host: null };
  }, [currentStation]);

  if (!isOpen || !currentStation) return null;

  const techStatus: TechnicalStreamStatus = currentStation.technicalStreamStatus || 'not_configured';
  const colors = technicalStreamStatusColors[techStatus] || technicalStreamStatusColors.not_configured;
  const showTechBanner = techStatus !== 'active';
  const isFallback = !!fallbackInfo;
  const showStreamError = !!streamError;

  const volumeIcon =
    volume === 0 ? 'ri-volume-mute-line'
    : volume < 33 ? 'ri-volume-down-line'
    : volume < 66 ? 'ri-volume-up-line'
    : 'ri-volume-up-fill';

  return (
    <div className="fixed inset-0 z-[80] flex flex-col justify-end" onClick={onClose}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${animating ? 'opacity-100' : 'opacity-0'}`}
      ></div>

      {/* Sheet */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative bg-foreground-950 rounded-t-3xl max-h-[95vh] overflow-y-auto transition-transform duration-300 ${animating ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 16px), 16px)' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-foreground-700 rounded-full"></div>
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-foreground-800 hover:bg-foreground-700 text-foreground-400 hover:text-background-50 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* ═══ Technical Status Banner ═══ */}
        {showTechBanner && !isFallback && !showStreamError && (
          <div className={`mx-5 mb-3 px-4 py-3 rounded-2xl flex items-start gap-3 ${colors.bg}`}>
            <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center shrink-0`}>
              <i className={`${currentStation.technicalStreamStatus === 'provisioning' ? 'ri-loader-4-line animate-spin' : 'ri-information-line'} ${colors.text} text-base`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`${colors.text} text-sm font-semibold`}>
                {technicalStreamStatusLabels[techStatus]}
              </p>
              <p className={`${colors.text} text-xs mt-0.5 opacity-70 leading-relaxed`}>
                {technicalStreamStatusMessages[techStatus]}
              </p>
            </div>
          </div>
        )}

        {/* ═══ Fallback Banner ═══ */}
        {isFallback && (
          <div className="mx-5 mb-3 px-4 py-3 rounded-2xl flex items-start gap-3 bg-amber-100">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <i className="ri-shuffle-line text-amber-700 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-amber-800 text-sm font-semibold">
                {fallbackInfo?.originalStationName} no está transmitiendo
              </p>
              <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                Ahora estás escuchando <strong>RFW Radio</strong>.
              </p>
              <button
                onClick={dismissFallback}
                className="mt-2 text-xs font-semibold text-amber-700 underline cursor-pointer"
              >
                Entendido
              </button>
            </div>
          </div>
        )}

        {/* ═══ Stream Error Banner ═══ */}
        {showStreamError && (
          <div className="mx-5 mb-3 px-4 py-3 rounded-2xl flex items-start gap-3 bg-red-100">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <i className="ri-error-warning-line text-red-600 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-red-800 text-sm font-semibold">{streamError?.stationName}</p>
              <p className="text-red-700 text-xs mt-0.5 leading-relaxed">{streamError?.message}</p>
              <button
                onClick={dismissStreamError}
                className="mt-2 text-xs font-semibold text-red-700 underline cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        {/* ═══ Logo + Station Info ═══ */}
        <div className="flex flex-col items-center px-6 pt-2">
          {/* Logo */}
          <div className="relative">
            <div className="w-32 h-32 rounded-3xl overflow-hidden bg-foreground-800 shadow-xl border-2 border-foreground-700">
              <img src={currentStation.logoUrl} alt={currentStation.name} className="w-full h-full object-cover" />
            </div>
            {currentStation.isLive && techStatus === 'active' && (
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold whitespace-nowrap shadow-lg">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                EN VIVO
              </span>
            )}
            {isFallback && (
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500 text-white text-[11px] font-bold whitespace-nowrap shadow-lg">
                <i className="ri-shuffle-line text-[11px]"></i>
                RFW RADIO
              </span>
            )}
          </div>

          <h2 className="text-xl font-bold text-background-50 mt-5 text-center">{currentStation.name}</h2>
          <p className="text-foreground-400 text-sm mt-0.5">{currentStation.frequency}</p>

          {/* Program / Metadata */}
          {techStatus === 'active' && currentStation.currentMetadata && (
            <div className="mt-3 text-center">
              <p className="text-background-50/80 text-sm font-medium">{metadata.program}</p>
              {metadata.host && <p className="text-foreground-500 text-xs mt-0.5">con {metadata.host}</p>}
            </div>
          )}

          {/* Listener count */}
          {techStatus === 'active' && (currentStation.listenerCount ?? 0) > 0 && (
            <p className="text-foreground-500 text-xs mt-1.5">
              <i className="ri-headphone-line mr-1"></i>
              {(currentStation.listenerCount ?? 0).toLocaleString()} oyentes
            </p>
          )}
        </div>

        {/* ═══ Playback Controls ═══ */}
        <div className="flex items-center justify-center gap-5 mt-6 px-6">
          {/* Prev */}
          <button
            onClick={() => {
              const idx = stations.findIndex((s) => s.id === currentStation.id);
              const prev = idx > 0 ? stations[idx - 1] : stations[stations.length - 1];
              changeStation(prev);
            }}
            className="w-11 h-11 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
            aria-label="Estación anterior"
          >
            <i className="ri-skip-back-fill text-xl"></i>
          </button>

          {/* Play / Pause */}
          <button
            onClick={toggle}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
              isPlaying
                ? 'bg-accent-500 hover:bg-accent-600 shadow-lg shadow-accent-500/40'
                : 'bg-primary-500 hover:bg-primary-600 shadow-lg shadow-primary-500/40'
            }`}
            aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
          >
            <i className={`text-background-50 text-3xl ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          </button>

          {/* Next */}
          <button
            onClick={() => {
              const idx = stations.findIndex((s) => s.id === currentStation.id);
              const next = idx < stations.length - 1 ? stations[idx + 1] : stations[0];
              changeStation(next);
            }}
            className="w-11 h-11 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
            aria-label="Siguiente estación"
          >
            <i className="ri-skip-forward-fill text-xl"></i>
          </button>
        </div>

        {/* ═══ Volume ═══ */}
        <div className="flex items-center gap-3 px-6 mt-5">
          <button
            onClick={() => setVolume(Math.max(0, volume - 10))}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-volume-down-line text-base"></i>
          </button>
          <div className="flex-1 h-2 bg-foreground-700 rounded-full relative cursor-pointer">
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
            <div
              className="absolute inset-y-0 left-0 bg-primary-500 rounded-full transition-all"
              style={{ width: `${volume}%` }}
            ></div>
          </div>
          <button
            onClick={() => setVolume(Math.min(100, volume + 10))}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-volume-up-line text-base"></i>
          </button>
          <span className="text-background-50 text-xs font-semibold w-8 text-right">{volume}%</span>
        </div>

        {/* ═══ Actions Row ═══ */}
        <div className="flex items-center justify-center gap-4 mt-5 px-6">
          {/* Favorite */}
          <button
            onClick={() => setFavorite(!favorite)}
            className={`flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl transition-all cursor-pointer ${
              favorite ? 'bg-red-500/10 text-red-400' : 'bg-foreground-800 text-foreground-400 hover:text-background-50'
            }`}
          >
            <i className={`${favorite ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
            <span className="text-[10px] font-semibold whitespace-nowrap">Favorito</span>
          </button>

          {/* Share */}
          <button
            onClick={handleShare}
            className="flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl bg-foreground-800 text-foreground-400 hover:text-background-50 transition-colors cursor-pointer"
          >
            <i className="ri-share-line text-lg"></i>
            <span className="text-[10px] font-semibold whitespace-nowrap">Compartir</span>
          </button>

          {/* Open Radio Profile */}
          <Link
            to={`/radio/${currentStation.slug || currentStation.frequency.replace('.', '-')}`}
            onClick={onClose}
            className="flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl bg-foreground-800 text-foreground-400 hover:text-background-50 transition-colors cursor-pointer"
          >
            <i className="ri-external-link-line text-lg"></i>
            <span className="text-[10px] font-semibold whitespace-nowrap">Ver Perfil</span>
          </Link>
        </div>

        {/* ═══ Sleep Timer ═══ */}
        <div className="mt-5 px-6">
          <div className="flex items-center gap-2 mb-2.5">
            <i className="ri-timer-line text-foreground-500 text-sm"></i>
            <span className="text-foreground-400 text-[11px] font-semibold uppercase tracking-wider">Sleep Timer</span>
            {sleepTimer > 0 && sleepRemaining > 0 && (
              <span className="ml-auto text-accent-400 text-xs font-bold">{formatSleepTime(sleepRemaining)}</span>
            )}
          </div>
          <div className="flex gap-1.5">
            {SLEEP_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSleepTimer(opt.value)}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  sleepTimer === opt.value
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-foreground-800 text-foreground-400 hover:text-background-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ═══ Recent Stations ═══ */}
        {recentStations.length > 0 && (
          <div className="mt-5 px-6">
            <div className="flex items-center gap-2 mb-2.5">
              <i className="ri-history-line text-foreground-500 text-sm"></i>
              <span className="text-foreground-400 text-[11px] font-semibold uppercase tracking-wider">Recientes</span>
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
              {recentStations.map((station) => (
                <button
                  key={station.id}
                  onClick={() => changeStation(station)}
                  className="flex flex-col items-center gap-1.5 shrink-0 cursor-pointer group"
                >
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-foreground-800 border border-foreground-700 group-hover:border-primary-500/50 transition-all">
                    <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-[10px] text-foreground-400 group-hover:text-background-50 whitespace-nowrap max-w-[60px] truncate font-medium transition-colors">
                    {station.name.length > 8 ? `${station.name.slice(0, 7)}…` : station.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Station Switcher ═══ */}
        <div className="px-6 pb-6 mt-5">
          <button
            onClick={() => setShowStationSwitcher(!showStationSwitcher)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-foreground-800 hover:bg-foreground-700 text-foreground-400 hover:text-background-50 text-xs font-semibold transition-colors cursor-pointer"
          >
            <i className="ri-search-line"></i>
            Buscar estación
          </button>

          {showStationSwitcher && (
            <div className="mt-3">
              <div className="relative mb-2">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  placeholder="Nombre, frecuencia, ciudad..."
                  value={stationSearch}
                  onChange={(e) => setStationSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-xl text-background-50 text-sm placeholder-foreground-500 outline-none focus:border-primary-500 transition-colors"
                  autoFocus
                />
              </div>
              <div className="max-h-64 overflow-y-auto bg-foreground-900 border border-foreground-700 rounded-xl scrollbar-none">
                {filteredStations.length === 0 ? (
                  <p className="text-foreground-500 text-xs text-center py-4">Sin resultados</p>
                ) : (
                  filteredStations.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => {
                        changeStation(s);
                        setStationSearch('');
                        setShowStationSwitcher(false);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-foreground-800 transition-colors cursor-pointer text-left ${
                        currentStation.id === s.id ? 'bg-primary-500/10 border-l-2 border-primary-500' : 'border-l-2 border-transparent'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden bg-foreground-800 shrink-0">
                        <img src={s.logoUrl} alt={s.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${currentStation.id === s.id ? 'text-primary-400' : 'text-background-50'}`}>
                          {s.name}
                        </p>
                        <p className="text-[11px] text-foreground-500">{s.frequency} · {s.category}</p>
                      </div>
                      {currentStation.id === s.id && (
                        <i className="ri-check-line text-primary-400 text-sm shrink-0"></i>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}